import i18n from "i18next";
import HttpBackend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

export const SUPPORTED_LOCALES = ["en", "sl", "de", "hr", "sr", "it", "es", "fr", "ru", "tr"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/**
 * i18n bootstrap. Locales live under /public/locales/<lang>/<namespace>.json
 * and are lazy-loaded per route via i18next-http-backend.
 *
 * Locale resolution order:
 *   1. authenticated user.locale (set by AuthProvider via i18n.changeLanguage)
 *   2. localStorage (LanguageDetector)
 *   3. browser navigator.language
 *   4. fallback "en"
 */
void i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LOCALES as readonly string[] as string[],
    load: "languageOnly", // strip "en-US" → "en"
    ns: ["common", "auth", "exploded"],
    defaultNS: "common",
    // Keys are stored FLAT with literal dots (e.g. "support.submit.subject"). Disabling the
    // key separator makes every dotted key a literal lookup, so a key used both as a label
    // ("support.submit.subject") and as a parent ("support.submit.subject.placeholder") can
    // coexist without i18next building an object at the shared path ("returned an object
    // instead of string"). Locale JSON is fully flattened to match.
    keySeparator: false,
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      caches: ["localStorage"],
      lookupLocalStorage: "imp.locale",
    },
    backend: {
      loadPath: "/locales/{{lng}}/{{ns}}.json",
    },
    react: {
      useSuspense: false,
    },
  });

export default i18n;

// Exposed for src/lib/format.ts, which must know the SELECTED language but is a plain
// module called from helpers outside the React tree. A global beats importing i18n there:
// format.ts stays usable in unit tests that never bootstrap i18next.
(globalThis as { __IMP_I18N__?: typeof i18n }).__IMP_I18N__ = i18n;
